import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Lightbox from '../../components/ui/Lightbox';
import clubRobotiqueData from '../../data/clubRobotiqueData.json';
import {
  CpuChipIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

export default function ClubRobotiquePage() {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="max-w-5xl mx-auto space-y-12 animate-fade-in px-4">
      {/* Hero */}
      <Card variant="elevated" className="p-6 sm:p-10 flex flex-col md:flex-row items-center gap-8">
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-white shadow-acem-md flex-shrink-0 bg-acem-surface-container-low">
          <img
            src={clubRobotiqueData.imageHero}
            alt="Club de Robotique ACEM Fès"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 space-y-3 text-center md:text-left">
          <Badge variant="primary">ACEM Fès Sciences & Tech</Badge>
          <h1 className="font-title text-2xl sm:text-4xl font-extrabold text-acem-primary-dark">
            {clubRobotiqueData.titreHero || "Club de Robotique & Innovation"}
          </h1>
          <p className="text-xs sm:text-sm text-acem-text/85 leading-relaxed">
            {clubRobotiqueData.descriptionHero}
          </p>
        </div>
      </Card>

      {/* Objectifs */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-2">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 text-acem-primary" aria-hidden="true" />
            <span>Nos Objectifs</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {clubRobotiqueData.objectifs?.map((obj, idx) => (
            <Card key={idx} variant="default" className="p-5 text-center space-y-2">
              <div className="text-3xl">{obj.icone}</div>
              <h3 className="font-bold text-sm text-acem-text">{obj.titre}</h3>
              <p className="text-xs text-acem-muted">{obj.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Programme & Cours */}
      {clubRobotiqueData.programme && clubRobotiqueData.programme.length > 0 && (
        <section className="space-y-4">
          <div className="border-b border-acem-border pb-2">
            <h2 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2">
              <BookOpenIcon className="w-5 h-5 text-acem-primary" aria-hidden="true" />
              <span>Programme et Ateliers de Formation</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {clubRobotiqueData.programme.map((prog, idx) => (
              <Card key={idx} variant="elevated" className="p-5 space-y-3">
                <h3 className="font-title font-bold text-base text-acem-primary-dark border-b border-acem-border/50 pb-2">
                  {prog.titre}
                </h3>
                <ul className="text-xs text-acem-text/90 space-y-1.5 list-disc list-inside">
                  {prog.chapitres?.map((chap, cIdx) => (
                    <li key={cIdx}>{chap}</li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Projets en Cours */}
      {clubRobotiqueData.projetsEnCours && clubRobotiqueData.projetsEnCours.length > 0 && (
        <section className="space-y-6">
          <div className="border-b border-acem-border pb-2">
            <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark flex items-center gap-2">
              <RocketLaunchIcon className="w-6 h-6 text-acem-primary" aria-hidden="true" />
              <span>Nos Projets en Développement</span>
            </h2>
          </div>

          <div className="space-y-6">
            {clubRobotiqueData.projetsEnCours.map((proj, idx) => (
              <Card key={idx} variant="elevated" className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  <div
                    onClick={() => setSelectedImage(proj.image)}
                    className="h-48 rounded-xl overflow-hidden bg-acem-surface-container-low cursor-pointer group shadow-sm flex items-center justify-center"
                  >
                    <img
                      src={proj.image}
                      alt={proj.titre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <Badge variant="accent">Projet {idx + 1}</Badge>
                    <h3 className="font-title text-xl font-bold text-acem-primary-dark">
                      {proj.titre}
                    </h3>
                    <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed">
                      {proj.intro}
                    </p>
                  </div>
                </div>

                {proj.fonctionnalites && (
                  <div className="bg-acem-surface-container-low p-4 rounded-xl border border-acem-border/60 space-y-2">
                    <h4 className="font-bold text-xs text-acem-primary-dark uppercase tracking-wider">
                      {proj.fonctionnalitesTitle || "Fonctionnalités :"}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-acem-text">
                      {proj.fonctionnalites.map((feat, fIdx) => (
                        <div key={fIdx} className="flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-acem-primary flex-shrink-0" aria-hidden="true" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {proj.objectif && (
                  <p className="text-xs text-acem-muted pt-1">
                    <strong>Objectif :</strong> {proj.objectif}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Rejoindre le club CTA */}
      <Card variant="glass" className="p-8 text-center space-y-3 max-w-2xl mx-auto">
        <h3 className="font-title text-2xl font-bold text-acem-primary-dark">
          Passionné(e) de robotique et d'électronique ?
        </h3>
        <p className="text-xs sm:text-sm text-acem-muted max-w-md mx-auto leading-relaxed">
          Rejoignez l'équipe technique de l'ACEM Fès et concevez des solutions innovantes.
        </p>
        <div className="pt-2">
          <Button
            as="a"
            href="mailto:acemfes7@gmail.com"
            variant="primary"
            size="md"
            className="inline-flex items-center gap-2"
          >
            <EnvelopeIcon className="w-4 h-4" aria-hidden="true" />
            <span>Postuler : acemfes7@gmail.com</span>
          </Button>
        </div>
      </Card>

      {/* Lightbox */}
      {selectedImage && (
        <Lightbox
          src={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
